import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import type { Feature } from "@passio/api-client";
import { chainToolId } from "@passio/easyai-client";
import { api, ApiError } from "./http";
import { applyServerDefaults } from "./clientSettings";
import { setPublicApiHost, setPublicEasyAiOrigin } from "./clients";
import type { Me } from "./types";
import { sessionEvents, usageStore } from "./usage";
import { takeKeyLink } from "./keyLink";

/** Signs in with a `#key=` link's key when there is one, else loads the current session. */
async function loadMe(): Promise<Me> {
  const apiKey = takeKeyLink();
  if (apiKey) {
    try {
      return await api<Me>("POST", "/api/auth/key", { apiKey });
    } catch (e) {
      notifications.show({ color: "red", title: "Couldn't sign in with that link", message: e instanceof Error ? e.message : String(e), autoClose: false });
    }
  }
  return api<Me>("GET", "/api/me");
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  error: ApiError | null;
  refresh: () => Promise<unknown>;
  setMe: (me: Me) => void;
  logout: () => Promise<void>;
  hasFeature: (f: Feature) => boolean;
  /** Not switched off by an admin; chain-backed pages also need at least one enabled chain. */
  toolEnabled: (toolId: string) => boolean;
}

const Ctx = createContext<SessionValue | null>(null);

const CHAIN_TOOLS = new Set(["meal_plans", "gi_coach", "grocery_swaps"]);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery<Me, ApiError>({
    queryKey: ["me"],
    queryFn: loadMe,
    retry: (n, e) => n < 2 && e.status >= 500,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!q.data) return;
    applyServerDefaults(q.data.settings.defaultClientHeaders);
    setPublicApiHost(q.data.app.apiHost);
    setPublicEasyAiOrigin(q.data.app.easyAi.origin);
    usageStore.set({ tokensToday: q.data.usage.tokensToday, tokensPerDay: q.data.limits.tokensPerDay });
  }, [q.data]);

  useEffect(() => {
    const onLost = () => qc.invalidateQueries({ queryKey: ["me"] });
    sessionEvents.addEventListener("lost", onLost);
    return () => sessionEvents.removeEventListener("lost", onLost);
  }, [qc]);

  const value: SessionValue = {
    me: q.data ?? null,
    loading: q.isLoading,
    error: q.error ?? null,
    refresh: () => q.refetch(),
    setMe: (me) => qc.setQueryData(["me"], me),
    logout: async () => {
      await api("POST", "/api/auth/logout").catch(() => {});
      qc.setQueryData(["me"], null);
      qc.removeQueries({ queryKey: ["me"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    hasFeature: (f) => Boolean(q.data?.limits.features.includes(f)),
    toolEnabled: (id) => {
      const app = q.data?.app;
      if (!app || app.disabledTools.includes(id)) return false;
      if (!CHAIN_TOOLS.has(id)) return true;
      return app.easyAi.chains.some((c) => chainToolId(c) === id);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
